import { useNavigate } from "react-router-dom";

const services = [
  {
    icon: "ri-ship-line",
    title: "Sea Freight",
    subtitle: "FCL & LCL Solutions",
    desc: "Regular sailings between China and major ports worldwide. Cost-effective full and less-than-container load options.",
    badge: "From 14 days",
    color: "from-sky-50 to-blue-50",
    accent: "text-sky-600",
    badgeBg: "bg-sky-100 text-sky-700",
    image: "https://public.readdy.ai/ai/img_res/a57320fcccfb509503a15482f316ffa3.jpg",
  },
  {
    icon: "ri-plane-line",
    title: "Air Freight",
    subtitle: "Daily Flights",
    desc: "Fast and reliable air freight with daily flights connecting China and North America for time-sensitive shipments.",
    badge: "12–18 hrs",
    color: "from-amber-50 to-orange-50",
    accent: "text-amber-600",
    badgeBg: "bg-amber-100 text-amber-700",
    image: "https://readdy.ai/api/search-image?query=cargo%20airplane%20freight%20aircraft%20loading%20airport%20tarmac%20professional%20photography%20dramatic%20sky%20lighting%20wide%20body%20aircraft&width=600&height=360&seq=home_air_02&orientation=landscape",
  },
  {
    icon: "ri-flashlight-line",
    title: "Express Delivery",
    subtitle: "Door-to-Door Global",
    desc: "Global door-to-door express delivery with real-time tracking and optimized routes via FedEx, UPS, DHL & Aramex.",
    badge: "3–10 days",
    color: "from-green-50 to-emerald-50",
    accent: "text-green-600",
    badgeBg: "bg-green-100 text-green-700",
    image: "https://readdy.ai/api/search-image?query=express%20delivery%20courier%20van%20packages%20door%20to%20door%20delivery%20service%20professional%20photography%20urban%20street%20modern%20logistics%20last%20mile&width=600&height=360&seq=home_express_03&orientation=landscape",
  },
  {
    icon: "ri-truck-line",
    title: "Cross-Border Trucking",
    subtitle: "60 Trucks · USA · Canada · Mexico",
    desc: "60-truck fleet with full cross-border capability. USMCA compliance, in-house customs clearance, and real-time GPS tracking.",
    badge: "Same day–3 days",
    color: "from-rose-50 to-red-50",
    accent: "text-rose-600",
    badgeBg: "bg-rose-100 text-rose-700",
    image: "https://readdy.ai/api/search-image?query=semi%20truck%20highway%20trucking%20logistics%20freight%20transport%20professional%20photography%20dramatic%20sky%20highway%20road%20USA%20interstate%20modern%20fleet&width=600&height=360&seq=home_truck_04&orientation=landscape",
  },
];

export default function FreightSection() {
  const navigate = useNavigate();

  return (
    <section className="py-16 bg-white">
      <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
          <div>
            <p className="text-amber-500 text-sm font-semibold uppercase tracking-widest mb-1">Global Logistics</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-stone-900 leading-tight">
              Sea · Air · Express<br className="hidden md:block" /> & Trucking Services
            </h2>
            <p className="text-stone-500 text-sm mt-2 max-w-xl">
              28 overseas warehouses across 6 regions. From FCL sea freight to same-day trucking — we move your cargo anywhere in the world.
            </p>
          </div>
          <button
            onClick={() => navigate("/freight-services")}
            className="hidden md:flex items-center gap-1.5 bg-amber-400 hover:bg-amber-500 text-white text-sm font-bold px-5 py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
          >
            View All Services <i className="ri-arrow-right-line"></i>
          </button>
        </div>

        {/* Service cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
          {services.map((s) => (
            <div
              key={s.title}
              onClick={() => navigate("/freight-services")}
              className={`bg-gradient-to-br ${s.color} rounded-2xl overflow-hidden cursor-pointer group hover:-translate-y-1 transition-transform duration-300 border border-stone-100`}
            >
              <div className="w-full h-36 overflow-hidden">
                <img
                  src={s.image}
                  alt={s.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 flex items-center justify-center bg-white/70 rounded-lg">
                      <i className={`${s.icon} ${s.accent} text-base`}></i>
                    </div>
                    <div>
                      <h3 className="font-extrabold text-stone-900 text-sm leading-none">{s.title}</h3>
                      <p className="text-xs text-stone-500 mt-0.5">{s.subtitle}</p>
                    </div>
                  </div>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${s.badgeBg}`}>{s.badge}</span>
                </div>
                <p className="text-stone-500 text-xs leading-relaxed">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Warehouse count banner */}
        <div
          onClick={() => navigate("/freight-services")}
          className="relative overflow-hidden bg-stone-900 rounded-2xl p-8 cursor-pointer group"
        >
          <div className="absolute inset-0 opacity-20">
            <img
              src="https://readdy.ai/api/search-image?query=world%20map%20logistics%20network%20global%20shipping%20routes%20connections%20professional%20dark%20background%20minimal%20abstract&width=1400&height=300&seq=home_freight_banner_05&orientation=landscape"
              alt="Global Network"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-white text-2xl font-extrabold mb-1">28 Overseas Warehouses Worldwide</h3>
              <p className="text-stone-400 text-sm">USA · Canada · Europe · Africa · Latin America · South Asia — fast local delivery from the nearest warehouse</p>
            </div>
            <div className="flex flex-wrap gap-3 shrink-0">
              {["🇺🇸 USA (5)", "🇨🇦 Canada (3)", "🇪🇺 Europe (7)", "🌍 Africa (5)", "🌎 Latin America (4)", "🌏 South Asia (4)"].map((r) => (
                <span key={r} className="bg-white/10 text-white text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap">{r}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile CTA */}
        <div className="mt-6 flex md:hidden justify-center">
          <button
            onClick={() => navigate("/freight-services")}
            className="flex items-center gap-2 bg-amber-400 hover:bg-amber-500 text-white font-bold px-8 py-3.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
          >
            View All Freight Services <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </div>
    </section>
  );
}
